if (!customElements.get('safi-bike-product')) {
  class SafiBikeProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.modal = this.querySelector('[data-bike-modal]');
      this.mainImage = this.querySelector('.safi-bike-gallery__main');
      this.modalImage = this.querySelector('.safi-bike-modal__image');
      this.total = this.querySelector('[data-bike-total]');
      this.message = this.querySelector('[data-bike-message]');
      this.addButton = this.querySelector('[data-bike-add]');
      this.openButton = this.querySelector('[data-bike-open]');
      this.delivery = 'In doos';

      try {
        this.variants = JSON.parse(this.querySelector('[data-bike-variants]')?.textContent || '[]');
      } catch (error) {
        this.variants = [];
      }

      this.currentVariant = this.variants.find((variant) => String(variant.id) === this.dataset.baseVariantId)
        || this.variants[0];

      this.querySelectorAll('[data-bike-thumb]').forEach((button) => {
        button.addEventListener('click', () => this.selectImage(button));
      });

      this.querySelectorAll('[data-bike-option]').forEach((input) => {
        input.addEventListener('change', () => this.selectVariant());
      });

      this.querySelectorAll('[data-bike-delivery]').forEach((button) => {
        button.addEventListener('click', () => this.selectDelivery(button.dataset.bikeDelivery));
      });

      this.querySelectorAll('[data-bike-addon-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.toggleAddon(button.closest('[data-bike-addon]')));
      });

      this.openButton?.addEventListener('click', () => this.openModal());
      this.querySelectorAll('[data-bike-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });
      this.modal?.addEventListener('close', () => this.unlockPage());
      this.addButton?.addEventListener('click', () => this.addToCart());

      this.updateTotal();
    }

    selectImage(button) {
      if (!button.dataset.imageSrc) return;
      this.setImages(button.dataset.imageSrc, button.dataset.imageAlt || '');
      this.querySelectorAll('[data-bike-thumb]').forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb === button);
      });
    }

    setImages(src, alt) {
      [this.mainImage, this.modalImage].forEach((image) => {
        if (!image || image.tagName !== 'IMG') return;
        image.src = src;
        image.removeAttribute('srcset');
        image.alt = alt;
      });
    }

    selectVariant() {
      const selectedOptions = Array.from(this.querySelectorAll('[data-bike-option]:checked'))
        .sort((a, b) => Number(a.dataset.bikeOption) - Number(b.dataset.bikeOption))
        .map((input) => input.value);

      const variant = this.variants.find((item) => item.options.every((value, index) => value === selectedOptions[index]));
      if (!variant) {
        this.setPurchaseState(false, 'Combinatie niet beschikbaar');
        return;
      }

      this.currentVariant = variant;
      this.dataset.baseVariantId = variant.id;
      this.dataset.basePriceCents = variant.price;

      this.querySelectorAll('[data-bike-option]:checked').forEach((input) => {
        const label = this.querySelector(`[data-bike-option-label="${input.dataset.bikeOption}"]`);
        if (label) label.textContent = input.value;
      });

      this.updatePrice(variant);
      this.updateInventory(variant);
      this.updateVariantImage(variant);
      this.setPurchaseState(variant.available, variant.available ? 'Nu personaliseren' : 'Uitverkocht');
      this.updateTotal();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    updatePrice(variant) {
      const price = this.querySelector('[data-bike-price]');
      const compare = this.querySelector('[data-bike-compare]');
      const discount = this.querySelector('[data-bike-discount]');
      const installment = this.querySelector('[data-bike-installment]');
      const comparePrice = Number(variant.compare_at_price || 0);

      if (price) price.textContent = this.formatMoney(variant.price);
      if (installment) installment.textContent = this.formatMoney(Math.round(variant.price / 3));

      if (compare) {
        compare.hidden = comparePrice <= variant.price;
        compare.textContent = comparePrice > variant.price ? this.formatMoney(comparePrice) : '';
      }

      if (discount) {
        const percentage = comparePrice > variant.price
          ? Math.round(((comparePrice - variant.price) / comparePrice) * 100)
          : 0;
        discount.hidden = percentage <= 0;
        discount.textContent = percentage > 0 ? `Bespaar ${percentage}%` : '';
      }
    }

    updateInventory(variant) {
      const stock = this.querySelector('[data-bike-stock]');
      const bar = this.querySelector('[data-bike-stock-bar]');
      const fallback = Number(this.dataset.fallbackStock || 8);
      const inventory = Number(variant.inventory_quantity);
      const count = Number.isFinite(inventory) && inventory > 0 ? inventory : fallback;

      if (!variant.available) {
        if (stock) stock.textContent = 'Tijdelijk uitverkocht';
        if (bar) bar.style.width = '0%';
        return;
      }

      if (stock) stock.textContent = `Slechts ${count} op voorraad`;
      if (bar) bar.style.width = `${Math.max(12, Math.min(100, count * 10))}%`;
    }

    updateVariantImage(variant) {
      const featuredImage = variant.featured_image;
      if (!featuredImage?.src) return;

      this.setImages(featuredImage.src, featuredImage.alt || '');
      this.querySelectorAll('[data-bike-thumb]').forEach((thumb) => {
        thumb.classList.toggle('is-active', String(thumb.dataset.mediaId) === String(featuredImage.id));
      });
    }

    setPurchaseState(available, label) {
      if (!this.openButton) return;
      this.openButton.disabled = !available;
      const text = this.openButton.querySelector('[data-bike-open-label]');
      if (text) text.textContent = label;
    }

    selectDelivery(value) {
      this.delivery = value;
      this.querySelectorAll('[data-bike-delivery]').forEach((button) => {
        const selected = button.dataset.bikeDelivery === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      this.querySelectorAll('[data-bike-delivery-label]').forEach((label) => {
        label.textContent = value;
      });
      this.updateTotal();
    }

    toggleAddon(addon) {
      if (!addon) return;
      const selected = !addon.classList.contains('is-selected');
      addon.classList.toggle('is-selected', selected);
      const input = addon.querySelector('[data-bike-addon-input]');
      const button = addon.querySelector('[data-bike-addon-toggle]');
      if (input) input.checked = selected;
      button?.setAttribute('aria-pressed', String(selected));
      this.updateTotal();
    }

    updateTotal() {
      const basePrice = Number(this.currentVariant?.price || this.dataset.basePriceCents || 0);
      const addonsPrice = Array.from(this.querySelectorAll('[data-bike-addon].is-selected'))
        .reduce((sum, addon) => sum + Number(addon.dataset.priceCents || 0), 0);
      const assemblyPrice = this.delivery === 'Rijklaar' ? Number(this.dataset.assemblyPriceCents || 0) : 0;
      if (this.total) this.total.textContent = this.formatMoney(basePrice + addonsPrice + assemblyPrice);
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'EUR';
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    }

    openModal() {
      if (!this.modal || !this.currentVariant?.available) return;
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
      document.documentElement.classList.add('safi-bike-modal-open');
    }

    closeModal() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close();
      else this.modal.removeAttribute('open');
      this.unlockPage();
    }

    unlockPage() {
      document.documentElement.classList.remove('safi-bike-modal-open');
    }

    async addToCart() {
      if (!this.currentVariant?.id || !this.addButton) return;
      const selectedAddons = Array.from(this.querySelectorAll('[data-bike-addon].is-selected'));
      const unlinkedAddon = selectedAddons.find((addon) => !addon.dataset.variantId);

      if (unlinkedAddon) {
        this.showMessage(`Koppel '${unlinkedAddon.dataset.addonTitle}' eerst aan een Shopify-product.`);
        return;
      }

      if (this.delivery === 'Rijklaar' && !this.dataset.assemblyVariantId) {
        this.showMessage('Koppel eerst het Shopify-product voor rijklaar maken in de thema-editor.');
        return;
      }

      const items = [{
        id: Number(this.currentVariant.id),
        quantity: 1,
        properties: { Levering: this.delivery }
      }];

      if (this.delivery === 'Rijklaar') {
        items.push({ id: Number(this.dataset.assemblyVariantId), quantity: 1 });
      }

      selectedAddons.forEach((addon) => {
        items.push({ id: Number(addon.dataset.variantId), quantity: 1 });
      });

      const label = this.addButton.querySelector('[data-bike-add-label]');
      this.addButton.disabled = true;
      if (label) label.textContent = 'Toevoegen...';
      this.showMessage('');

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('De gekozen producten konden niet worden toegevoegd.');
        window.location.href = window.routes?.cart_url || '/cart';
      } catch (error) {
        this.addButton.disabled = false;
        if (label) label.textContent = 'In mijn winkelwagen';
        this.showMessage(error.message);
      }
    }

    showMessage(text) {
      if (this.message) this.message.textContent = text;
    }
  }

  customElements.define('safi-bike-product', SafiBikeProduct);
}
